import React, { useState } from 'react';
import { Loader2, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import type { ActivityPart, TokenUsageSummary } from '../../types/chat';

interface ActivityGroupProps {
  parts: ActivityPart[];
}

function formatTokenCount(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Groups 2+ transient ActivityParts into a single compact row showing
 * all agents on one line with a shared spinner.
 */
function TransientActivityGroup({ parts }: ActivityGroupProps) {
  const agentMessages = parts.map((p) => {
    const msg = p.data.message.length > 40
      ? p.data.message.slice(0, 40) + '...'
      : p.data.message;
    return `${p.data.source} ${msg}`;
  });

  return (
    <div
      className="flex items-center gap-2 py-1 px-2 text-xs rounded-lg text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800"
      data-testid="activity-group-transient"
    >
      <Loader2 className="w-3 h-3 animate-spin text-emerald-500 flex-shrink-0" />
      <span className="font-medium">{parts.length} agents:</span>
      <span className="truncate">{agentMessages.join(' \u00b7 ')}</span>
    </div>
  );
}

function UsageRow({ label, usage }: { label: string; usage: TokenUsageSummary }) {
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
      <span className="font-medium text-gray-600 dark:text-gray-300 min-w-[80px]">{label}</span>
      {usage.durationMs > 0 && <span>{formatDuration(usage.durationMs)}</span>}
      <span>
        {formatTokenCount(usage.inputTokens)} / {formatTokenCount(usage.outputTokens)} tokens
      </span>
    </div>
  );
}

/**
 * Groups 2+ agent-complete ActivityParts into a single summary row
 * with expandable per-agent breakdown.
 */
function CompletionActivityGroup({ parts }: ActivityGroupProps) {
  const [expanded, setExpanded] = useState(false);

  // Aggregate stats
  let totalDuration = 0;
  let totalTokens = 0;
  for (const p of parts) {
    if (p.data.usage) {
      totalDuration += p.data.usage.durationMs;
      totalTokens += p.data.usage.totalTokens;
    }
  }

  return (
    <div
      className="py-1.5 px-3 text-xs rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800"
      data-testid="activity-group-completion"
    >
      <button
        type="button"
        className="flex items-center gap-3 w-full text-left bg-transparent border-0 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        <span className="font-medium text-emerald-700 dark:text-emerald-400">
          {parts.length} agents completed
        </span>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          {totalDuration > 0 && <span>{formatDuration(totalDuration)}</span>}
          {totalTokens > 0 && <span>{formatTokenCount(totalTokens)} tokens</span>}
        </div>
        <span className="ml-auto text-gray-400 dark:text-gray-500">
          {expanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </span>
      </button>

      {expanded && (
        <div className="mt-1.5 pl-6 space-y-0.5 border-l-2 border-emerald-200 dark:border-emerald-800 ml-1">
          {parts.map((p) => {
            const usage = p.data.usage;
            if (!usage) {
              return (
                <div key={p.id} className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-600 dark:text-gray-300 min-w-[80px]">
                    {p.data.source}
                  </span>
                  <span>completed</span>
                </div>
              );
            }
            return <UsageRow key={p.id} label={p.data.source} usage={usage} />;
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Routes to the appropriate grouped display based on activity type.
 */
export function ActivityGroup({ parts }: ActivityGroupProps) {
  const isCompletion = parts[0]?.data.activityType === 'agent-complete';
  if (isCompletion) {
    return <CompletionActivityGroup parts={parts} />;
  }
  return <TransientActivityGroup parts={parts} />;
}
